import { Link } from 'react-router-dom';
import Navbar from '../../home/components/Navbar';
import Footer from '../../home/components/Footer';
import PageHero from '../../../components/feature/PageHero';
import SEOHead from '../../../components/feature/SEOHead';
import CountdownTimer from '../../../components/feature/CountdownTimer';

export default function RecuperacaoSinistrosPage() {
  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Recuperação de Sinistros",
    "provider": {
      "@type": "Organization",
      "name": "IBX Brasil"
    },
    "description": "Assessoria especializada em recuperação de sinistros com acompanhamento técnico, negociação com seguradoras e maximização de indenizações.",
    "serviceType": "Recuperação de Sinistros",
    "areaServed": "BR"
  };

  return (
    <>
      <SEOHead
        title="Recuperação de Sinistros | Assessoria Especializada"
        description="Maximize suas indenizações com assessoria especializada em recuperação de sinistros. Acompanhamento técnico, negociação com seguradoras e defesa dos seus direitos."
        canonical="https://ibxbrasil.com.br/servicos/recuperacao-sinistros"
        ogImage="https://readdy.ai/api/search-image?query=professional%20insurance%20claims%20specialist%20reviewing%20damage%20assessment%20documents%20with%20client%20modern%20office%20consultation%20meeting%20professional%20atmosphere%20clean%20background%20realistic%20business%20photography&width=1200&height=630&seq=seo-recuperacao-sinistros-service&orientation=landscape"
        ogType="service"
        schema={serviceSchema}
      />
      <div className="min-h-screen bg-white">
        <Navbar />
        
        {/* Hero Section */}
        <PageHero
          badge="SERVIÇOS • RECUPERAÇÃO DE SINISTROS"
          title="Recuperação Técnica de Sinistros"
          highlightedWord="Sinistros"
          highlightColor="orange"
          subtitle="Acompanhamento especializado em processos de sinistro, garantindo seus direitos contratuais e maximizando o ressarcimento com suporte técnico completo."
          benefits={[
            'Análise técnica de cobertura aplicável',
            'Acompanhamento em todas as etapas',
            'Negociação com seguradoras',
            'Maximização do ressarcimento'
          ]}
          ctaText="Falar no WhatsApp"
          ctaLink="https://wa.me/5577988080505?text=Olá! Preciso de ajuda com recuperação de sinistro."
          ctaSecondary={{
            text: 'Solicitar Análise',
            link: '#contato'
          }}
          backgroundImage="https://readdy.ai/api/search-image?query=professional%20insurance%20claims%20recovery%20specialist%20analyzing%20documents%20in%20modern%20office%20technical%20support%20business%20meeting%20atmosphere%20professional%20lighting&width=1920&height=700&seq=recuperacao-sinistros-hero-001&orientation=landscape"
          overlayOpacity="medium"
        />

        {/* Why Need Support */}
        <section className="py-20">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                Por Que Ter Suporte Técnico em Sinistros?
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Processos complexos que exigem conhecimento especializado
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-white border border-gray-200 rounded-lg p-8">
                <div className="w-14 h-14 bg-orange-100 rounded-lg flex items-center justify-center mb-4">
                  <i className="ri-file-shield-line text-2xl text-orange-600"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Defesa dos Seus Direitos</h3>
                <p className="text-gray-600 leading-relaxed">
                  Análise técnica das cláusulas contratuais para garantir que a seguradora cumpra todas as obrigações previstas na apólice.
                </p>
              </div>

              <div className="bg-white border border-gray-200 rounded-lg p-8">
                <div className="w-14 h-14 bg-orange-100 rounded-lg flex items-center justify-center mb-4">
                  <i className="ri-money-dollar-circle-line text-2xl text-orange-600"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Maximização de Indenizações</h3>
                <p className="text-gray-600 leading-relaxed">
                  Estruturação técnica da documentação e argumentação para garantir o valor justo de indenização.
                </p>
              </div>

              <div className="bg-white border border-gray-200 rounded-lg p-8">
                <div className="w-14 h-14 bg-orange-100 rounded-lg flex items-center justify-center mb-4">
                  <i className="ri-time-line text-2xl text-orange-600"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Agilidade no Processo</h3>
                <p className="text-gray-600 leading-relaxed">
                  Conhecimento dos procedimentos internos das seguradoras para acelerar análise e liberação de valores.
                </p>
              </div>

              <div className="bg-white border border-gray-200 rounded-lg p-8">
                <div className="w-14 h-14 bg-orange-100 rounded-lg flex items-center justify-center mb-4">
                  <i className="ri-shield-check-line text-2xl text-orange-600"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Evitar Recusas Indevidas</h3>
                <p className="text-gray-600 leading-relaxed">
                  Análise prévia de cobertura e orientação técnica para evitar negativas baseadas em interpretações equivocadas.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Process Section */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                Processo de Recuperação
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Acompanhamento técnico em todas as etapas
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-6">
              <div className="bg-white rounded-lg p-6 border-t-4 border-orange-600">
                <div className="w-12 h-12 bg-orange-600 text-white rounded-full flex items-center justify-center font-bold text-xl mb-4 mx-auto">
                  1
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2 text-center">Análise de Cobertura</h3>
                <p className="text-gray-600 text-sm text-center leading-relaxed">
                  Verificação técnica se o sinistro está coberto pela apólice
                </p>
              </div>

              <div className="bg-white rounded-lg p-6 border-t-4 border-orange-600">
                <div className="w-12 h-12 bg-orange-600 text-white rounded-full flex items-center justify-center font-bold text-xl mb-4 mx-auto">
                  2
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2 text-center">Abertura do Aviso</h3>
                <p className="text-gray-600 text-sm text-center leading-relaxed">
                  Comunicação formal à seguradora com documentação inicial
                </p>
              </div>

              <div className="bg-white rounded-lg p-6 border-t-4 border-orange-600">
                <div className="w-12 h-12 bg-orange-600 text-white rounded-full flex items-center justify-center font-bold text-xl mb-4 mx-auto">
                  3
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2 text-center">Documentação</h3>
                <p className="text-gray-600 text-sm text-center leading-relaxed">
                  Organização e envio de todos os documentos exigidos
                </p>
              </div>

              <div className="bg-white rounded-lg p-6 border-t-4 border-orange-600">
                <div className="w-12 h-12 bg-orange-600 text-white rounded-full flex items-center justify-center font-bold text-xl mb-4 mx-auto">
                  4
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2 text-center">Acompanhamento</h3>
                <p className="text-gray-600 text-sm text-center leading-relaxed">
                  Monitoramento ativo da análise pela seguradora
                </p>
              </div>

              <div className="bg-white rounded-lg p-6 border-t-4 border-orange-600">
                <div className="w-12 h-12 bg-orange-600 text-white rounded-full flex items-center justify-center font-bold text-xl mb-4 mx-auto">
                  5
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2 text-center">Liberação</h3>
                <p className="text-gray-600 text-sm text-center leading-relaxed">
                  Verificação de valores e condições de pagamento
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Types of Claims */}
        <section className="py-20">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                Tipos de Sinistros Atendidos
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Suporte especializado em todas as modalidades
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="flex gap-4 bg-white border border-gray-200 rounded-lg p-6">
                <i className="ri-car-line text-3xl text-orange-600 mt-1"></i>
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">Automóveis</h3>
                  <p className="text-gray-600 text-sm">Colisão, roubo, furto, incêndio e danos a terceiros</p>
                </div>
              </div>

              <div className="flex gap-4 bg-white border border-gray-200 rounded-lg p-6">
                <i className="ri-home-line text-3xl text-orange-600 mt-1"></i>
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">Patrimoniais</h3>
                  <p className="text-gray-600 text-sm">Incêndio, roubo, danos elétricos e eventos naturais</p>
                </div>
              </div>

              <div className="flex gap-4 bg-white border border-gray-200 rounded-lg p-6">
                <i className="ri-shield-user-line text-3xl text-orange-600 mt-1"></i>
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">Vida e Acidentes</h3>
                  <p className="text-gray-600 text-sm">Morte, invalidez e despesas médicas</p>
                </div>
              </div>

              <div className="flex gap-4 bg-white border border-gray-200 rounded-lg p-6">
                <i className="ri-user-line text-3xl text-orange-600 mt-1"></i>
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">Responsabilidade Civil</h3>
                  <p className="text-gray-600 text-sm">Danos causados a terceiros em diversas situações</p>
                </div>
              </div>

              <div className="flex gap-4 bg-white border border-gray-200 rounded-lg p-6">
                <i className="ri-briefcase-line text-3xl text-orange-600 mt-1"></i>
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">Empresariais</h3>
                  <p className="text-gray-600 text-sm">Lucros cessantes, equipamentos e riscos operacionais</p>
                </div>
              </div>

              <div className="flex gap-4 bg-white border border-gray-200 rounded-lg p-6">
                <i className="ri-ship-line text-3xl text-orange-600 mt-1"></i>
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">Transportes</h3>
                  <p className="text-gray-600 text-sm">Cargas, mercadorias e responsabilidade do transportador</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Our Support */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                Como Atuamos na Recuperação
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Suporte técnico completo durante todo o processo
              </p>
            </div>

            <div className="space-y-6">
              <div className="bg-white rounded-lg p-8 border-l-4 border-orange-600">
                <div className="flex items-start gap-6">
                  <div className="w-16 h-16 bg-orange-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-file-search-line text-3xl text-orange-600"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3">Análise Técnica do Sinistro</h3>
                    <p className="text-gray-600 leading-relaxed mb-4">
                      Avaliação detalhada do evento, verificação de cobertura contratual e identificação de todos os direitos aplicáveis.
                    </p>
                    <div className="grid md:grid-cols-2 gap-3">
                      <div className="flex items-start gap-2">
                        <i className="ri-check-line text-orange-600 text-lg mt-0.5"></i>
                        <span className="text-gray-600 text-sm">Revisão das cláusulas da apólice</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <i className="ri-check-line text-orange-600 text-lg mt-0.5"></i>
                        <span className="text-gray-600 text-sm">Verificação de exclusões aplicáveis</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <i className="ri-check-line text-orange-600 text-lg mt-0.5"></i>
                        <span className="text-gray-600 text-sm">Análise de franquias e participações</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <i className="ri-check-line text-orange-600 text-lg mt-0.5"></i>
                        <span className="text-gray-600 text-sm">Identificação de coberturas adicionais</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg p-8 border-l-4 border-orange-600">
                <div className="flex items-start gap-6">
                  <div className="w-16 h-16 bg-orange-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-file-list-3-line text-3xl text-orange-600"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3">Estruturação de Documentação</h3>
                    <p className="text-gray-600 leading-relaxed mb-4">
                      Organização completa de todos os documentos necessários para análise ágil e aprovação pela seguradora.
                    </p>
                    <div className="grid md:grid-cols-2 gap-3">
                      <div className="flex items-start gap-2">
                        <i className="ri-check-line text-orange-600 text-lg mt-0.5"></i>
                        <span className="text-gray-600 text-sm">Boletim de ocorrência e laudos técnicos</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <i className="ri-check-line text-orange-600 text-lg mt-0.5"></i>
                        <span className="text-gray-600 text-sm">Orçamentos e notas fiscais</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <i className="ri-check-line text-orange-600 text-lg mt-0.5"></i>
                        <span className="text-gray-600 text-sm">Fotos e registros do evento</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <i className="ri-check-line text-orange-600 text-lg mt-0.5"></i>
                        <span className="text-gray-600 text-sm">Documentos pessoais e contratuais</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg p-8 border-l-4 border-orange-600">
                <div className="flex items-start gap-6">
                  <div className="w-16 h-16 bg-orange-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-discuss-line text-3xl text-orange-600"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3">Negociação com Seguradora</h3>
                    <p className="text-gray-600 leading-relaxed mb-4">
                      Interlocução técnica direta com reguladores e analistas para defesa dos seus interesses.
                    </p>
                    <div className="grid md:grid-cols-2 gap-3">
                      <div className="flex items-start gap-2">
                        <i className="ri-check-line text-orange-600 text-lg mt-0.5"></i>
                        <span className="text-gray-600 text-sm">Argumentação técnica fundamentada</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <i className="ri-check-line text-orange-600 text-lg mt-0.5"></i>
                        <span className="text-gray-600 text-sm">Contestação de recusas indevidas</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <i className="ri-check-line text-orange-600 text-lg mt-0.5"></i>
                        <span className="text-gray-600 text-sm">Negociação de valores de indenização</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <i className="ri-check-line text-orange-600 text-lg mt-0.5"></i>
                        <span className="text-gray-600 text-sm">Acompanhamento de perícias técnicas</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg p-8 border-l-4 border-orange-600">
                <div className="flex items-start gap-6">
                  <div className="w-16 h-16 bg-orange-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-customer-service-2-line text-3xl text-orange-600"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3">Acompanhamento Contínuo</h3>
                    <p className="text-gray-600 leading-relaxed mb-4">
                      Monitoramento ativo do andamento do processo até a liberação final dos valores.
                    </p>
                    <div className="grid md:grid-cols-2 gap-3">
                      <div className="flex items-start gap-2">
                        <i className="ri-check-line text-orange-600 text-lg mt-0.5"></i>
                        <span className="text-gray-600 text-sm">Atualizações periódicas do status</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <i className="ri-check-line text-orange-600 text-lg mt-0.5"></i>
                        <span className="text-gray-600 text-sm">Cobrança de prazos contratuais</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <i className="ri-check-line text-orange-600 text-lg mt-0.5"></i>
                        <span className="text-gray-600 text-sm">Suporte em vistorias e perícias</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <i className="ri-check-line text-orange-600 text-lg mt-0.5"></i>
                        <span className="text-gray-600 text-sm">Orientação em cada etapa</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Final */}
        <section className="py-20 bg-gradient-to-br from-orange-600 to-orange-700">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <CountdownTimer location="paginas-servicos" />
            <h2 className="text-4xl font-bold text-white mb-6">
              Recupere Seus Direitos com Especialistas
            </h2>
            <p className="text-xl text-orange-100 mb-8">
              Entre em contato e tenha suporte técnico especializado para recuperação de valores
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <a
                href="https://wa.me/5577988080505"
                target="_blank"
                rel="noopener noreferrer"
                className="px-8 py-4 bg-white text-orange-600 rounded-lg font-semibold hover:bg-orange-50 transition-colors duration-300 flex items-center gap-2 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-whatsapp-line text-2xl"></i>
                <span>Falar no WhatsApp</span>
              </a>
              <a
                href="/contato"
                className="inline-flex items-center gap-2 px-8 py-4 bg-strategic-green-500 text-white font-semibold rounded-lg transition-all duration-300 hover:bg-strategic-green-600 hover:scale-105 hover:shadow-xl whitespace-nowrap"
              >
                Solicitar Suporte
                <i className="ri-arrow-right-line text-xl"></i>
              </a>
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
}